'use client';

import type React from 'react';
import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * Props for the portal video modal (YouTube embed).
 *
 * Optional `iframeTestId` / `modalTestId` support Playwright `data-test` hooks.
 */
export interface VideoModalProps {
  iframeTestId?: string;
  iframeTitle?: string;
  modalTestId?: string;
  onClose: () => void;
  open: boolean;
  videoUrl: string | null;
}

/**
 * Converts a YouTube URL to an embed URL with autoplay.
 *
 * @param {string} url - YouTube watch URL, youtu.be URL, or embed URL
 * @returns {string} YouTube embed URL with autoplay parameters
 */
export const convertToEmbedUrl = (url: string): string => {
  if (!url) return '';

  if (url.includes('/embed/')) {
    const urlObj = new URL(url);
    urlObj.searchParams.set('autoplay', '1');
    urlObj.searchParams.set('rel', '0');
    urlObj.searchParams.set('modestbranding', '1');
    return urlObj.toString();
  }

  let videoId = '';
  const watchMatch = url.match(/[?&]v=([^&]+)/);
  const shortMatch = url.match(/youtu\.be\/([^?]+)/);

  if (watchMatch) {
    videoId = watchMatch[1];
  } else if (shortMatch) {
    videoId = shortMatch[1];
  }

  if (!videoId) return '';

  return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
};

export const VideoModal: React.FC<VideoModalProps> = ({
  iframeTestId = 'marketing-video-iframe',
  iframeTitle = 'Video player',
  modalTestId = 'marketing-video-modal',
  onClose,
  open,
  videoUrl,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousOverflowRef = useRef<string>('');

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!open) {
      setIsLoading(true);
      return;
    }

    previousOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    if (closeButtonRef.current) {
      closeButtonRef.current.focus();
    }

    return () => {
      document.body.style.overflow = previousOverflowRef.current;
    };
  }, [open]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onClose]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  if (!mounted || !open || !videoUrl) {
    return null;
  }

  const embedUrl = convertToEmbedUrl(videoUrl);

  if (!embedUrl) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-9999 flex items-center justify-center bg-black/70 p-4"
      data-test={modalTestId}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Video player"
    >
      <div className="max-w-laptop relative aspect-video w-full overflow-hidden rounded-lg bg-black">
        {isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-white border-t-transparent" />
          </div>
        )}
        <iframe
          src={embedUrl}
          className="h-full w-full"
          data-test={iframeTestId}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          onLoad={handleIframeLoad}
          title={iframeTitle}
        />
      </div>
    </div>,
    document.body,
  );
};
